import { KanbanProcessoStruct } from '../../../../shared/services/structs/vendas/kanban-processo.struct';
import { EtapaKanbanStruct } from '../../../../shared/services/structs/vendas/etapa-kanban.struct';
import { TipoEtapaKanbanEnum } from '../../../../shared/enums/vendas/tipo-etapa-kanban.enum';

export interface KanbanAviso {
    idEtapaKanban: number | null;
    tipo: 'warning' | 'info';
    mensagem: string;
}

/** A etapa "Modelo" é alcançada por ação dedicada, nunca por movimentação configurada. */
function ehModelo(etapa: EtapaKanbanStruct): boolean {
    return etapa.chave === 'Modelo';
}

/**
 * Computa os avisos de consistência do fluxo (ignora processos inativos):
 * - etapa não-final sem saída; etapa final com saídas (info);
 * - etapa não-inicial inalcançável;
 * - processo sem inicial; processo sem final.
 *
 * Escopo por tipo de etapa:
 * - Fluxo principal: entra em todas as validações.
 * - Especiais (exceto Modelo): só na validação de alcance — são destino configurável, então
 *   um especial sem ninguém apontando para ele é inalcançável. Não entram na de saída:
 *   encerrar é justamente não ter saída.
 * - Modelo e marcadores de "Envio e retirada": fora de todas. A entrada e a saída deles são
 *   ação dedicada / evento de rotina externa, não movimentação configurada.
 *
 * Perfil de movimentação NÃO entra na validação: lista vazia = todos podem mover.
 */
export function validarKanban(processos: KanbanProcessoStruct[]): KanbanAviso[] {
    const avisos: KanbanAviso[] = [];
    const ativos = processos.filter(p => p.isAtivo);

    // Destinos que recebem alguma movimentação (de etapas de processos ativos).
    const destinosRecebidos = new Set<number>();
    for (const p of ativos) {
        for (const s of p.etapas) {
            for (const mov of s.movimentacoes) {
                destinosRecebidos.add(mov.idEtapaKanbanDestino);
            }
        }
    }
    // Obs.: movimentação sem perfil NÃO é aviso — lista vazia significa "todos podem mover".

    for (const p of ativos) {
        const fluxoPrincipal = p.etapas.filter(
            s => s.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal
        );

        if (!fluxoPrincipal.some(s => s.isInicial)) {
            avisos.push({ idEtapaKanban: null, tipo: 'warning', mensagem: `O processo "${p.descricaoTipo}" não tem etapa inicial.` });
        }
        // "Processo sem etapa final" considera também os especiais (seed já marca Cancelado/
        // Entregue/Não entregue como isFinal=true — eles de fato encerram o card fora do fluxo).
        if (!p.etapas.some(s => s.isFinal)) {
            avisos.push({ idEtapaKanban: null, tipo: 'warning', mensagem: `O processo "${p.descricaoTipo}" não tem etapa final.` });
        }

        for (const s of fluxoPrincipal) {
            if (!s.isFinal && s.movimentacoes.length === 0) {
                avisos.push({ idEtapaKanban: s.idEtapaKanban, tipo: 'warning', mensagem: `"${s.nome}" (${p.descricaoTipo}) não tem para onde mover e não está marcada como final.` });
            }
            // Saída para etapa especial não conflita com "final": o aviso considera apenas
            // destinos do próprio fluxo principal.
            if (s.isFinal) {
                const saidasNoFluxo = s.movimentacoes.filter(mov =>
                    fluxoPrincipal.some(alvo => alvo.idEtapaKanban === mov.idEtapaKanbanDestino)
                );
                if (saidasNoFluxo.length > 0) {
                    avisos.push({ idEtapaKanban: s.idEtapaKanban, tipo: 'info', mensagem: `"${s.nome}" (${p.descricaoTipo}) é final, mas ainda tem saídas configuradas.` });
                }
            }
            if (!s.isInicial && !destinosRecebidos.has(s.idEtapaKanban)) {
                avisos.push({ idEtapaKanban: s.idEtapaKanban, tipo: 'warning', mensagem: `"${s.nome}" (${p.descricaoTipo}) é inalcançável — nenhuma etapa leva até ela.` });
            }
        }

        // Especiais participam só da validação de alcance (Modelo fica de fora).
        const especiais = p.etapas.filter(
            s => s.idTipoEtapaKanban === TipoEtapaKanbanEnum.Especial && !ehModelo(s)
        );
        for (const s of especiais) {
            if (!destinosRecebidos.has(s.idEtapaKanban)) {
                avisos.push({ idEtapaKanban: s.idEtapaKanban, tipo: 'warning', mensagem: `"${s.nome}" (${p.descricaoTipo}) é inalcançável — nenhuma etapa do fluxo tem esta etapa especial como destino.` });
            }
        }
    }

    return avisos;
}
